/**
 * Song-Editor: Metadaten, ChordPro-Quelltext und Live-Vorschau.
 *
 * Es gibt keinen Speichern-Knopf – jede Aenderung wird nach kurzer Pause
 * automatisch gesichert. Das ist der kuerzeste Weg von "Neu" zum fertigen Song.
 */
import type { View } from '../app.ts';
import { navigate, setSidebar } from '../app.ts';
import type { Song } from '../types.ts';
import { deleteSong, getSong, newId, saveSong } from '../db/repo.ts';
import { parseChordPro } from '../chordpro/parse.ts';
import { renderSheet } from '../chordpro/render.ts';
import { createAudioPanel } from '../ui/audio-panel.ts';
import { button, field, h, textArea, textInput } from '../ui/dom.ts';
import { sheetLyrics } from '../features/prompter-text.ts';
import { icon } from '../ui/icons.ts';
import { pickImageFiles, shrinkImage } from '../ui/image.ts';
import { toast } from '../ui/toast.ts';

const SAVE_DELAY = 600;
const PREVIEW_DELAY = 150;

export async function renderSongEditor(params: Record<string, string>): Promise<View> {
  const id = params.id as string;
  const existing = await getSong(id);
  if (!existing) {
    return {
      title: 'Song nicht gefunden',
      back: '#/',
      body: h('div', { class: 'empty', text: 'Dieser Song wurde geloescht.' }),
    };
  }

  let song: Song = existing;
  let saveTimer = 0;
  let previewTimer = 0;
  let dirty = false;

  const status = h('span', { class: 'topbar__sub', text: 'Gespeichert' });

  function touch(patch: Partial<Song>): void {
    song = { ...song, ...patch };
    dirty = true;
    status.textContent = 'Änderungen …';
    clearTimeout(saveTimer);
    saveTimer = window.setTimeout(() => void flush(), SAVE_DELAY);
  }

  async function flush(): Promise<void> {
    if (!dirty) return;
    dirty = false;
    song = await saveSong(song);
    status.textContent = 'Gespeichert';
  }

  /* ------------------------------------------------------- Metadaten */

  const titleInput = textInput({
    value: song.title,
    placeholder: 'Titel',
    onInput: (value) => {
      touch({ title: value });
      titleHeading.textContent = value || 'Ohne Titel';
    },
  });

  const metaCard = h(
    'section',
    { class: 'card' },
    field('Titel', titleInput),
    h(
      'div',
      { class: 'grid-2' },
      field(
        'Komponist / Interpret',
        textInput({
          value: song.artist,
          placeholder: 'z. B. Wes Montgomery',
          onInput: (value) => touch({ artist: value }),
        }),
      ),
      field(
        'Tonart',
        textInput({
          value: song.key,
          placeholder: 'z. B. Bb',
          onInput: (value) => touch({ key: value }),
        }),
      ),
      field(
        'Tempo (BPM)',
        textInput({
          value: song.tempo === null ? '' : String(song.tempo),
          placeholder: 'z. B. 132',
          type: 'number',
          inputMode: 'numeric',
          onInput: (value) => touch({ tempo: value === '' ? null : Number(value) }),
        }),
      ),
      field(
        'Taktart',
        textInput({
          value: song.timeSignature,
          placeholder: 'z. B. 4/4',
          onInput: (value) => touch({ timeSignature: value }),
        }),
      ),
    ),
    field(
      'Notizen',
      textArea({
        value: song.notes,
        rows: 3,
        placeholder: 'Form, Solisten, Einzähler …',
        onInput: (value) => touch({ notes: value }),
      }),
    ),
  );

  /* ------------------------------------------------------------- Audio */

  /**
   * Aenderungen am Audio werden sofort gesichert, nicht ueber die Debounce-
   * Wartezeit der Textfelder: ein Tipper auf "Entfernen" oder den Lock soll
   * nicht erst 600 ms warten. `song` wird dabei synchron mitgefuehrt, damit
   * eine laufende Texteingabe (touch/flush) die Audio-Aenderung nicht
   * ueberschreibt.
   */
  const audioPanel = createAudioPanel({
    song,
    allowAdd: true,
    onChange: async (audio) => {
      song = await saveSong({ ...song, audio });
    },
  });

  const audioCard = h(
    'section',
    { class: 'card' },
    h('h2', { class: 'card__title', text: 'Audio' }),
    audioPanel.element,
  );

  /* ------------------------------------------------------------ Fotos */

  /**
   * Fotos oder Scans des Lead Sheets. Wie beim Audio sofort gesichert, nicht
   * ueber die Wartezeit der Textfelder. Die Reihenfolge ist die Lesereihenfolge
   * im Sheet; Pfeile verschieben eine Seite nach vorn oder hinten.
   */
  const photoGrid = h('div', { class: 'photo-grid' });
  const photoStatus = h('p', { class: 'hint' });

  async function savePhotos(photos: Song['photos']): Promise<void> {
    await flush();
    song = await saveSong({ ...song, photos });
    paintPhotos();
  }

  async function addPhotos(): Promise<void> {
    const files = await pickImageFiles();
    if (files.length === 0) return;
    photoStatus.textContent = files.length === 1 ? 'Foto wird eingelesen …' : `${files.length} Fotos werden eingelesen …`;
    const added: NonNullable<Song['photos']> = [];
    for (const file of files) {
      try {
        // 2000 px an der langen Kante: Noten und Akkorde bleiben gut lesbar.
        added.push({ id: newId(), src: await shrinkImage(file, 2000, 700_000) });
      } catch (err) {
        console.error(err);
        toast('Ein Foto konnte nicht gelesen werden.', 3000);
      }
    }
    photoStatus.textContent = '';
    if (added.length === 0) return;
    await savePhotos([...(song.photos ?? []), ...added]);
    toast(added.length === 1 ? 'Foto gesichert' : `${added.length} Fotos gesichert`);
  }

  function movePhoto(index: number, delta: number): void {
    const photos = [...(song.photos ?? [])];
    const target = index + delta;
    if (target < 0 || target >= photos.length) return;
    const [item] = photos.splice(index, 1);
    if (item) photos.splice(target, 0, item);
    void savePhotos(photos);
  }

  function removePhoto(index: number): void {
    if (!confirm('Dieses Foto entfernen?')) return;
    void savePhotos((song.photos ?? []).filter((_, i) => i !== index));
  }

  function paintPhotos(): void {
    const photos = song.photos ?? [];
    photoGrid.replaceChildren(
      ...photos.map((photo, i) =>
        h(
          'div',
          { class: 'photo-grid__item' },
          h('img', { attrs: { src: photo.src, alt: `Seite ${i + 1}`, draggable: 'false' } }),
          h('span', { class: 'photo-grid__page', text: `Seite ${i + 1}` }),
          h(
            'div',
            { class: 'photo-grid__actions' },
            photos.length > 1 && i > 0
              ? button('‹', () => movePhoto(i, -1), { class: 'btn btn--icon btn--small', title: 'Nach vorn' })
              : null,
            photos.length > 1 && i < photos.length - 1
              ? button('›', () => movePhoto(i, 1), { class: 'btn btn--icon btn--small', title: 'Nach hinten' })
              : null,
            h(
              'button',
              {
                class: 'btn btn--icon btn--small',
                attrs: { type: 'button', title: 'Entfernen', 'aria-label': 'Foto entfernen' },
                on: { click: () => removePhoto(i) },
              },
              icon('trash'),
            ),
          ),
        ),
      ),
    );
    photoGrid.hidden = photos.length === 0;
  }
  paintPhotos();

  const photoCard = h(
    'section',
    { class: 'card' },
    h('h2', { class: 'card__title', text: 'Fotos vom Lead Sheet' }),
    h('p', {
      class: 'hint',
      text: 'Fotografiere oder wähle Seiten aus. Sie erscheinen im Sheet unter dem Text – oder allein, wenn es keinen Text gibt – und scrollen mit dem Auto-Scroll mit.',
    }),
    photoGrid,
    photoStatus,
    h('div', { class: 'btn-row' }, button('Fotos hinzufügen', () => void addPhotos(), { class: 'btn' })),
  );

  /* ------------------------------------------- Quelltext und Vorschau */

  const source = textArea({
    value: song.body,
    class: 'editor-source',
    placeholder: '[Dm7]Text  [G7]weiter …',
    onInput: (value) => {
      touch({ body: value });
      paintTakeOver();
      clearTimeout(previewTimer);
      previewTimer = window.setTimeout(paintPreview, PREVIEW_DELAY);
    },
  });

  const titleHeading = h('h2', { class: 'sheet__title', text: song.title || 'Ohne Titel' });
  const previewBody = h('div', { class: 'sheet-body' });
  const preview = h(
    'div',
    { class: 'editor-preview' },
    h(
      'div',
      { class: 'sheet-stack' },
      h('div', { class: 'sheet' }, h('div', { class: 'sheet__head' }, titleHeading), previewBody),
    ),
  );

  function paintPreview(): void {
    renderSheet(parseChordPro(source.value), previewBody);
  }
  paintPreview();

  const editorCard = h(
    'section',
    { class: 'card' },
    h('h2', { class: 'card__title', text: 'Lead Sheet' }),
    h(
      'div',
      { class: 'editor-split' },
      h(
        'div',
        {},
        source,
        h('p', {
          class: 'hint',
          html:
            'Einfach schreiben, die App erkennt Akkorde von selbst. ' +
            'Gross- und Kleinschreibung ist egal, aus <code>ebm</code> wird <code>Ebm</code>.',
        }),
        h('p', {
          class: 'hint',
          html:
            '<strong>Form:</strong> <code>Intro: ebm, Eb Eb, dm7, D,</code><br>' +
            'Ein Komma trennt die Takte. <strong>Zwei Kommas</strong> hängen den ' +
            'Akkord an den laufenden Takt an: <code>Am,, Dm, G,</code> ergibt ' +
            '<code>|Am Dm|G|</code>. Ein Leerzeichen macht dasselbe – nimm, was ' +
            'dir beim Schreiben leichter fällt. Im Raster steht zwischen zwei ' +
            'Akkorden eines Takts ein feiner Strich, die Taktgrenze bleibt der ' +
            'kräftige. Die Beschriftung vorne ist freiwillig; lässt du sie in ' +
            'der nächsten Zeile weg, rückt sie bündig darunter. ' +
            'Wiederholung ans Zeilenende: <code>Prechorus: ebm, Eb,  4x</code>. ' +
            'Leere Takte: <code>Instr.: , , , ,</code>',
        }),
        h('p', {
          class: 'hint',
          html:
            '<strong>Gesang:</strong> Akkorde in die Zeile darüber, ' +
            'an der Stelle, wo sie klingen sollen.<br>' +
            '<code>Ebm&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Ab</code><br>' +
            '<code>Montag früh im Proberaum</code>',
        }),
      ),
      preview,
    ),
  );

  /* ---------------------------------------------------------- Songtext */

  /*
   * Eigenes Feld fuer den reinen Text – fuer Teleprompter und PDF. Wer den
   * Text schon im Lead Sheet unter den Akkorden hat, uebernimmt ihn mit
   * einem Tipper und bearbeitet ihn dann hier weiter.
   */
  const lyricsInput = textArea({
    value: song.lyrics ?? '',
    rows: 10,
    class: 'textarea editor-lyrics',
    placeholder: 'Strophe 1:\nHier steht der Text …\n\nRefrain:\n…',
    onInput: (value) => {
      touch({ lyrics: value });
      paintTakeOver();
    },
  });
  const takeOver = button(
    'Aus dem Lead Sheet übernehmen',
    () => {
      const text = sheetLyrics(song.body)
        .map((l) => (l.kind === 'label' ? `${l.text}:` : l.kind === 'gap' ? '' : l.text))
        .join('\n');
      lyricsInput.value = text;
      touch({ lyrics: text });
      paintTakeOver();
      toast('Text übernommen – hier weiter bearbeiten');
    },
    { class: 'btn btn--small' },
  );
  function paintTakeOver(): void {
    takeOver.hidden = lyricsInput.value.trim() !== '' || sheetLyrics(song.body).every((l) => l.kind !== 'text');
  }
  paintTakeOver();

  const lyricsCard = h(
    'section',
    { class: 'card' },
    h('h2', { class: 'card__title', text: 'Songtext' }),
    h('p', {
      class: 'hint',
      text: 'Nur der Text, ohne Akkorde – für Teleprompter und PDF. Leere Zeile = Pause, eine Zeile wie „Refrain:“ = Abschnitt. Leer gelassen, nehmen beide den Text aus dem Lead Sheet.',
    }),
    lyricsInput,
    h('div', { class: 'btn-row', style: { marginTop: '10px' } }, takeOver),
  );

  const deleteButton = button(
    'Song löschen',
    async () => {
      if (!confirm(`"${song.title || 'Ohne Titel'}" wirklich löschen?`)) return;
      dirty = false;
      clearTimeout(saveTimer);
      await deleteSong(song.id);
      toast('Song gelöscht');
      navigate('#/');
    },
    { class: 'btn btn--danger btn--small' },
  );

  const body = h(
    'div',
    {},
    metaCard,
    photoCard,
    audioCard,
    editorCard,
    lyricsCard,
    h('div', { class: 'btn-row', style: { justifyContent: 'flex-end' } }, deleteButton),
  );

  return {
    title: 'Song bearbeiten',
    back: '#/',
    body,
    actions: [
      status,
      // Vollbild: das fertige Raster, dort mit dem Stift anmerken.
      button('Vollbild ✎', () => void flush().then(() => {
        // Seitenleiste weg: das Sheet bekommt die ganze Breite.
        setSidebar('closed');
        navigate(`#/sheet/${song.id}`);
      }), {
        class: 'btn btn--ghost',
        title: 'Im Vollbild ansehen und mit dem Stift bearbeiten',
      }),
    ],
    cleanup: () => {
      clearTimeout(saveTimer);
      clearTimeout(previewTimer);
      void flush();
      audioPanel.destroy();
    },
  };
}
